import React, { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { AsYouType } from 'libphonenumber-js'
import { useForm, useWatch } from 'react-hook-form'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { usStatesSelector, caProvincesSelector, userSelector } from '@src/selectors/app'
import { useDispatchAsync } from '@src/utils'
import { NavigatorContext } from '@src/utils/navigatorContext'
import { NavigationContainer } from '@src/screens/Common/containers'
import { AddressCountries } from '@src/types'
import { getCountryCodeByCountry } from '@src/utils/phoneInput'
import { AddressForm, AddressFormData } from '@src/components/Address/AddressForm'
import { ScrollableAvoidKeyboard } from '@src/components'
import { Button, Text } from '@src/components/base'
import { OnboardingStackParamsList } from '@src/navigation/types'
import { useLazyScreenPreload } from '@src/navigation/utils'

export const InsuranceAddress = () => {
  const dispatch = useDispatchAsync()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)

  const navigation = useNavigation<StackNavigationProp<OnboardingStackParamsList>>()

  const user = useSelector(userSelector)
  const usStates = useSelector(usStatesSelector)
  const caProvinces = useSelector(caProvincesSelector)

  const address = user?.address

  const styles = useStyleSheet(themedStyle)

  const insets = useSafeAreaInsets()

  useLazyScreenPreload('VideoCallConfirmation')

  const defaultCountry = (address?.country as AddressCountries | undefined) || AddressCountries.Us

  const defaultValues: AddressFormData = {
    country: defaultCountry,
    name: address?.name || `${user?.firstName} ${user?.lastName}`,
    street: address?.street || '',
    street2: address?.street2 || '',
    city: address?.city || '',
    state: address?.state || '',
    zipCode: address?.zipCode || '',
    phoneNumber: new AsYouType(getCountryCodeByCountry(defaultCountry)).input(
      user?.phoneNumber || '',
    ),
  }

  const form = useForm({ mode: 'onTouched', defaultValues })
  const {
    handleSubmit,
    formState: { isValid },
    control,
  } = form

  const country = useWatch({ control, name: 'country' })
  const state = useWatch({ control, name: 'state' })

  const states = (country === AddressCountries.Us ? usStates : caProvinces).map((item) => ({
    text: item.abbreviation,
    value: item.abbreviation,
  }))

  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async ({ phoneNumber, ...address }: AddressFormData) => {
    if (isSubmitting) {
      return
    }

    const stateFromServer = states.find((item) => item.text.toLowerCase() === state.toLowerCase())

    setIsSubmitting(true)
    enableLoader()
    try {
      await dispatch({
        type: 'address/update',
        payload: {
          ...address,
          state: stateFromServer?.value,
        },
      })

      if (phoneNumber) {
        await dispatch({ type: 'users/update', payload: { phoneNumber } })
      }

      navigation.navigate('VideoCallConfirmation')
    } finally {
      disableLoader()
      setIsSubmitting(false)
    }
  }

  const allowCountryChange = defaultValues.country !== AddressCountries.Us

  return (
    <NavigationContainer
      allowBackNavigation={false}
      title="What is your address"
      style={styles.container}
    >
      <ScrollableAvoidKeyboard
        style={styles.container}
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        bounces={false}
      >
        <Text type="regular" style={styles.description}>
          We use this information for filing insurance claims.
        </Text>
        <AddressForm allowCountryChange={allowCountryChange} form={form} />
        <Button
          type="primary"
          size="block"
          disabled={!isValid}
          style={styles.button}
          onPress={handleSubmit(onSubmit)}
          accessibilityLabel="Save"
          allowPressWhenDisabled
        >
          Continue
        </Button>
      </ScrollableAvoidKeyboard>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  content: {
    flexGrow: 1,
    flexShrink: 0,
    paddingHorizontal: 16,
    justifyContent: 'space-between',
  },
  description: {
    maxWidth: 260,
    textAlign: 'center',
    alignSelf: 'center',
    marginTop: 8,
    marginBottom: 24,
  },
  button: {
    marginTop: 24,
    marginBottom: 16,
  },
})
